<template>
  <div class="day-detail-workout">
    <!-- reorder icons -->
    <div v-if="canReorder" class="schedule-order">
      <icon-button
        v-show="enableOrderUp"
        icon="keyboard_arrow_up"
        classes="-ico-only -no-container action-button"
        :icons-outlined="false"
        label="Move up in order"
        :submitting="reordering || reorderDisabled"
        submitting-label="Reordering"
        aria-live="polite"
        @click-action="updateOrder('up')"
      />
      <icon-button
        v-show="enableOrderDown"
        icon="keyboard_arrow_down"
        classes="-ico-only -no-container action-button"
        :icons-outlined="false"
        label="Move up in order"
        :submitting="reordering || reorderDisabled"
        submitting-label="Reordering"
        aria-live="polite"
        @click-action="updateOrder('down')"
      />
    </div>

    <!-- image -->
    <div class="scheduled-workout">
      <div class="workout-thumb">
        <a class="" :href="planDaySchedule.schedulable.url">
          <card-src-set-image
            :img-prefix="imageUrlPrefix"
            :image="planDaySchedule.schedulable.image"
            :title="planDaySchedule.schedulable.title"
          />
        </a>
      </div>

      <div class="workout-title">
        <!-- error state, only shows if delete fails -->
        <callout v-if="error" type="error">
          <div>{{ error }}</div>
        </callout>

        <strong v-if="!enableOptionalButton && selectedType.name !== 'Required'"
          >{{ selectedType.name }} -</strong
        >
        {{ planDaySchedule.schedulable.title }}
      </div>
    </div>

    <!-- actions -->
    <div class="schedule-actions">
      <!-- type fade in / out when type changes -->
      <auto-fade-help v-if="showNewType" :title="selectedType.name" />

      <!-- required, optional, extra credit toggle -->
      <icon-button
        v-if="enableOptionalButton"
        :icon="selectedType.icon"
        classes="-ico-only -no-container action-button"
        label="Set workout as required, optional, or extra credit"
        :submitting="updating"
        submitting-label="Saving workout"
        :icons-outlined="false"
        aria-live="polite"
        @click-action="updateType"
      />

      <!-- delete with modal confirm -->
      <delete-button
        v-if="enableDelete"
        :modal-id="`routine-schedule-${planDaySchedule.id}-delete-modal`"
        modal-heading="Delete Workout?"
        modal-text="Are you sure you want to delete this workout from your routine? "
        label="Delete Workout"
        classes="-ico-only -no-container action-button"
        :icons-outlined="false"
        :submitting="deleting"
        @delete-action="deletePlanDaySchedule"
      />
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue'
import { mapState } from 'pinia'
import { useUserAuthStore } from '@/stores/user-auth'
import { fbApi } from '@/utils/http-api'
import CardSrcSetImage from '@/components/images/card-src-set-image.vue'
import IconButton from '@/components/buttons/icon-button.vue'
import DeleteButton from '@/components/buttons/delete-button.vue'
import Callout from '@/components/notifications/callout.vue'
import AutoFadeHelp from '@/components/tool-tips/auto-fade-help.vue'

/**
 * Header row for week with optional expand / collapse actions
 */
export default defineComponent({
  name: 'EditPlanDaySchedule',
  components: { CardSrcSetImage, IconButton, DeleteButton, Callout, AutoFadeHelp },
  props: {
    // TODO: define flow type
    planDaySchedule: {
      type: Object,
      required: true,
    },

    // turn on / off actions
    enableOrderUp: {
      type: Boolean,
      default: false,
    },
    enableOrderDown: {
      type: Boolean,
      default: false,
    },
    enableOptionalButton: {
      type: Boolean,
      default: true,
    },
    enableDelete: {
      type: Boolean,
      default: true,
    },

    // parent component can show buttons, but disable reordering from being clicked
    // this is to stop multiple different scheduled items from being reordered at the same time
    reorderDisabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: [
    // update plan day schedule
    'update-action',
    // delete plan day schedule
    'delete-action',
    // reorder plan day schedule
    'reorder-start',
    'reorder-complete',
    'reorder-error',
  ],

  data() {
    return {
      // updating state
      // only available types for routines
      updating: false,
      showNewType: false,
      typeMap: {
        1: {
          name: 'Required',
          icon: 'radio_button_unchecked',
        },
        2: {
          name: 'Extra Credit',
          icon: 'stars',
        },
        3: {
          name: 'Optional',
          icon: 'trip_origin',
        },
      },

      // delete state
      deleting: false,
      error: '',

      // reordering
      reordering: false,
    }
  },

  computed: {
    ...mapState(useUserAuthStore, ['cdn']),

    canReorder() {
      return !!(this.enableOrderUp || this.enableOrderDown)
    },

    imageUrlPrefix() {
      // get siteassets url from page load, allows CDN url to be added
      const siteAssetsPrefix = this.cdn.siteAssetsPrefix || '/siteassets'

      return `${siteAssetsPrefix}/${this.planDaySchedule.schedulable.imgDir}`
    },

    selectedType() {
      if (this.typeMap[this.planDaySchedule.typeId]) {
        return this.typeMap[this.planDaySchedule.typeId]
      }

      // choice 1 / 2 only for read only views
      return {
        name: 'Option',
        icon: '',
      }
    },
  },

  methods: {
    // this is not intelligent, just returns a hard coded next type
    getNextType(typeId) {
      // if required, return EC
      if (typeId === 1) {
        return 2
      }

      // if EC, return optional
      if (typeId === 2) {
        return 3
      }

      return 1
    },

    /**
     * Toggle showNewType, then toggle off after 2 seconds
     * animation is 500ms and class addition is 1500ms via auto-fade-helper.vue
     * mount method
     */
    fadeTypeDisplay() {
      this.showNewType = true
      setTimeout(() => {
        this.showNewType = false
      }, 2000)
    },

    /**
     * Move scheduled item order field up / down by one point.
     *
     * Events
     * - reorder-start
     * - reorder-complete
     * - reorder-error
     *
     * @param direction up,down
     * @returns {Promise<boolean>}
     */
    async updateOrder(direction) {
      // do not process delete twice
      if (this.reordering) {
        return true
      }

      // calc new orders (can only mov up / down one point)
      const prevOrder = this.planDaySchedule.order
      const newOrder =
        direction === 'up' ? this.planDaySchedule.order - 1 : this.planDaySchedule.order + 1
      const eventPayload = {
        id: this.planDaySchedule.id,
        schedulableType: this.planDaySchedule.schedulableType,
        schedulableId: this.planDaySchedule.schedulableId,
        planDayId: this.planDaySchedule.planDayId,
        prevOrder,
        newOrder,
      }

      // state to loading, letting parent component know
      this.$emit('reorder-start', eventPayload)

      this.reordering = true
      this.error = ''
      // end set state

      try {
        const res = await fbApi.put(
          `/routines/${this.planDaySchedule.planId}/plan-day-schedule/${this.planDaySchedule.id}`,
          {
            json: {
              order: newOrder,
            },
          },
        )

        if (!res || !res.body || !res.body.data) {
          throw Error('Error reordering schedulable item')
        }

        // pass to parent component to update
        this.$emit('reorder-complete', eventPayload)
      } catch (e) {
        this.error = 'Error reordering schedulable item'

        // flip flop the order so it can be undone
        const newOrder = eventPayload.prevOrder
        const prevOrder = eventPayload.newOrder
        eventPayload.newOrder = newOrder
        eventPayload.prevOrder = prevOrder
        this.$emit('reorder-error', eventPayload)
      }

      // cancel loading
      this.reordering = false
    },

    /**
     * Determine the "next" type for the plan day schedule, XHR request,
     * then emit event for parent state.
     * @returns {Promise<boolean>}
     */
    async updateType() {
      // do not process delete twice
      if (this.updating || this.showNewType) {
        return true
      }

      // reset state
      this.updating = true
      this.error = ''

      // get next type_id for plan day schedule
      const currTypeId = this.planDaySchedule.typeId
      const nextTypeId = this.getNextType(this.planDaySchedule.typeId)

      // instantly update UI, pass to parent component to update
      // not waiting for XHR to complete
      this.$emit('update-action', {
        id: this.planDaySchedule.id,
        planDaySchedule: {
          ...this.planDaySchedule,
          typeId: nextTypeId,
        },
      })

      // fade in new type
      this.fadeTypeDisplay()

      try {
        const res = await fbApi.put(
          `/routines/${this.planDaySchedule.planId}/plan-day-schedule/${this.planDaySchedule.id}`,
          {
            json: {
              type_id: nextTypeId,
            },
          },
        )

        if (!res || !res.body || !res.body.data) {
          throw Error('Error updating schedulable item')
        }
      } catch (e) {
        this.error = 'Error updating schedulable item'

        // if error, revert UI to original order
        this.$emit('update-action', {
          id: this.planDaySchedule.id,
          planDaySchedule: {
            ...this.planDaySchedule,
            typeId: currTypeId,
          },
        })
      }

      // cancel loading
      this.updating = false
    },

    /**
     * Delete plan day schedule with emitted event for parent state.
     * @returns {Promise<boolean>}
     */
    async deletePlanDaySchedule() {
      // do not process delete twice
      if (this.deleting) {
        return true
      }

      // reset state
      this.deleting = true
      this.error = ''

      try {
        const res = await fbApi.delete(
          `/routines/${this.planDaySchedule.planId}/plan-day-schedule/${this.planDaySchedule.id}`,
        )

        if (!res || !res.body || !res.body.status) {
          throw Error('Error deleting schedulable item')
        }

        // pass to parent component to remove from list
        this.$emit('delete-action', {
          id: this.planDaySchedule.id,
        })
      } catch (e) {
        this.error = 'Error deleting schedulable item'
      }

      // cancel loading
      this.deleting = false
    },
  },
})
</script>
